import { ChevronRight } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { sanitizeForJsonLd } from '../../../utils/sanitize';

export default function ViewMeta({ postContent, imageList }) {
  const safeName = sanitizeForJsonLd(postContent.name, 200);
  const safeDescription =
    sanitizeForJsonLd(postContent.description, 160) ||
    sanitizeForJsonLd(`${postContent.name} - ${postContent.category}`, 160);
  const pageTitle = `${safeName} - DealSpotNG Classifieds`;
  const pageDescription = safeDescription;
  const ogImage =
    imageList[0] ||
    'https://dealspotng.dabaras.com/assets/images/dealspotngicon.png';
  const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
  const canonicalUrl = postContent.id
    ? `https://dealspotng.dabaras.com/ad/${postContent.id}`
    : pageUrl;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: safeName,
    description: pageDescription,
    image:
      Array.isArray(imageList) && imageList.length > 0 ? imageList : [ogImage],
    category: sanitizeForJsonLd(postContent.category, 100),
    offers: {
      '@type': 'Offer',
      price: postContent.price,
      priceCurrency: 'NGN',
      availability: 'https://schema.org/InStock',
      url: canonicalUrl,
    },
    brand: {
      '@type': 'Organization',
      name: 'DealSpotNG',
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://dealspotng.dabaras.com/',
      },
      ...(postContent.category
        ? [
            {
              '@type': 'ListItem',
              position: 2,
              name: postContent.category,
              item: `https://dealspotng.dabaras.com/search?category=${encodeURIComponent(postContent.category)}`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: safeName,
              item: canonicalUrl,
            },
          ]
        : [
            {
              '@type': 'ListItem',
              position: 2,
              name: safeName,
              item: canonicalUrl,
            },
          ]),
    ],
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta
          name="keywords"
          content={`${safeName}, ${postContent.category || ''}, buy, sell, DealSpotNG, classifieds, listings, Nigeria, marketplace, deals`}
        />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="product" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:alt" content={safeName} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="DealSpotNG" />
        <meta property="og:locale" content="en_NG" />
        <meta property="product:price:amount" content={postContent.price} />
        <meta property="product:price:currency" content="NGN" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={ogImage} />
        <meta name="twitter:image:alt" content={safeName} />

        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd)}
        </script>
      </Helmet>
      <nav className="viewBreadcrumb">
        <Link to="/">Home</Link>
        <ChevronRight size={14} className="viewBreadcrumbSep" />
        {postContent.category && (
          <>
            <Link
              to={`/search?category=${encodeURIComponent(postContent.category)}`}
            >
              {postContent.category}
            </Link>
            <ChevronRight size={14} className="viewBreadcrumbSep" />
          </>
        )}
        <span className="viewBreadcrumbCurrent">{postContent.name}</span>
      </nav>
    </>
  );
}
